import Vue from 'vue';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import FileAPI from 'file-api';
import { guid } from '@/mixins/guid';
import { cssGenerator } from '@/mixins/css-generator';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import dropsLoader from '@/mixins/components/drops-loader';
import fonts from '@editor/fonts.json';
import path from 'path';
import { siteBuilder } from '@/mixins/site-builder';

const File = FileAPI.File;

export default Vue.component('theme-fonts', {
  template: require('./template.html'),
  name: 'theme-fonts',
  mixins: [guid, cssGenerator, siteBuilder, uploader],
  components: {
    uploadProgress,
    dropsLoader,
  },
  computed: {
    ...mapGetters(['currentSite', 'currentPage']),
    fonts() {
      const input = new RegExp(this.filterFonts, 'gi');
      return _.filter(this.currentSite.fonts, f => f.match(input));
    },
    allFonts() {
      const list = fonts.webSafe.concat(this.currentSite.fonts).sort();
      const f = new RegExp(this.searchFonts, 'gi');
      return _.filter(list, font => font.match(f));
    },
    fontStyles() {
      const selector = _.find(this.currentSite.mediaQueries, {
        selector: 'body',
      });
      const property = selector ? _.find(selector.properties, { property: 'font-family' }) : false;
      return property;
    },
  },
  data() {
    return {
      paginate: ['fontlist'],
      s3Url: process.env.S3_ASSETS_URL,
      fontfile: null,
      loading: false,
      loaders: {
        fonts: false,
        defaultFont: false,
      },
      edit: {
        fonts: false,
        defaultFont: false,
      },
      changed: {
        fonts: false,
        defaultFont: false,
      },
      preparing: false,
      confirmDeleteFont: false,
      activeFont: null,
      filterFonts: '',
      searchFonts: '',
      showFonts: '',
      display: {
        fontFamily: '',
      },
      initial: {
        fonts: [],
        pageCSS: null,
        mediaQueries: [],
        defaultFont: null,
      },
    };
  },
  created() {
    this.checkNewValues();
  },
  mounted() {
    this.callCssGenerator();
  },
  watch: {
    'currentSite.pageCSS': function c(css) {
      this.changed.defaultFont = this.initial.pageCSS !== css;
    },
    updateInitial(from) {
      if (from === 'selectionText') {
        this.setInitials();
      }
    },
    showModal(o) {
      if (o) {
        this.setInitials();
      } else {
        this.onCloseModal();
      }
    },
  },
  props: ['show-modal', 'toggle-modal', 'update-initial'],
  methods: {
    setInitials() {
      this.initial.pageCSS = _.cloneDeep(this.currentSite.pageCSS);
      this.initial.mediaQueries = _.cloneDeep(this.currentSite.mediaQueries);
      this.display.fontFamily = this.getDefaultFont();
    },
    callCssGenerator() {
      return this.generateCSS({
        drp: this.currentSite,
        hideTags: true,
      });
    },
    checkNewValues() {
      const values = [];
      if (!this.currentSite.mediaQueries) {
        values.push({
          key: 'mediaQueries',
          value: [],
        });
      }
      if (!this.currentSite.globalCSS) {
        values.push({
          key: 'globalCSS',
          value: true,
        });
      }
      if (!this.currentSite.pageCSS) {
        values.push({
          key: 'pageCSS',
          value: true,
        });
      }
      if (values.length > 0) {
        this.$store.dispatch('setNewSiteValues', {
          site: this.currentSite,
          vm: this,
          values,
        });
      }
      if (!this.fontStyles) {
        this.$store.dispatch('changeDropletCss', {
          drp: this.currentSite,
          val: fonts.defaultFont,
          viewport: 'desktop',
          property: 'font-family',
          selector: 'body',
          noId: true,
        });
      }
    },
    getDefaultFont() {
      return this.fontStyles.desktop || fonts.defaultFont;
    },
    async setFont(font) {
      this.$store.dispatch('changeDropletCss', {
        drp: this.currentSite,
        val: font,
        viewport: 'desktop',
        property: 'font-family',
        selector: 'body',
        noId: true,
      });
      this.display.fontFamily = font;
      this.callCssGenerator();
    },
    openUpload(type) {
      this.$refs[type].click();
    },
    uploadFont(e) {
      this.fontfile = [...e.target.files];
      this.$validator.validate('fontfile', this.fontfile).then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          this.preparing = true;
          const newFile = [...e.target.files];
          if (newFile.length > 0) {
            try {
              const fontFile = newFile[0];
              const base = path.basename(fontFile.name);
              const name = base.split('.')[0];

              const filePath = `/_fonts/${name}/${base}`;
              // upload font file
              await this.uploadFile({
                file: `${this._sitePath}${filePath}`,
                body: fontFile,
              });

              // To create a file
              const cssFile = await this.makeFontCSS(base);
              // upload css file
              await this.uploadFile({
                file: `${this._sitePath}/_fonts/${name}/font.css`,
                body: cssFile.buffer,
              });

              // add font to website fonts
              this.$store.dispatch('addSiteFont', name);

              // save updates
              await this.$lightning([{
                name: 'updateSiteAttr',
                payload: {
                  siteId: this.currentSite._id,
                  attr: 'fonts',
                  value: this.currentSite.fonts,
                },
              }]);
              this.$log.debug('successfully added new font');
            } catch (e) {
              if (!e.message.match(/aborted/gi)) {
                this.$log.error(e);
                await this._reportError({
                  error: e,
                  fn: 'uploadFont',
                });
              }
            } finally {
              this.preparing = false;
            }
          }
        }
        // reset value to null so we can get the "change" event to run
        // in any case. Even when the user selects the same file
        e.target.value = null;
      });
    },
    trashFont(font) {
      this.confirmDeleteFont = true;
      this.activeFont = font;
    },
    async deleteFont() {
      this.loaders.fonts = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        message: 'Successfully deleted font!',
      };
      try {
        this.$store.dispatch('removeSiteFont', this.activeFont);
        // delete from s3
        const fonts = await this._s3emptyFolder({
          path: `${this._customerFolder}/${this._sitePath}/_fonts/${this.activeFont}`,
        });
        this.$log.debug('Successfully deleted fonts: ', fonts);
        // save updates
        await this.$lightning([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'fonts',
            value: this.currentSite.fonts,
          },
        }]);
        this.$log.debug('successfully removed font');
      } catch (e) {
        this.$log.error(e);
        notif.type = 'error';
        notif.message = e.message || e;
        await this._reportError({
          error: e,
          fn: 'deleteFont',
        });
      } finally {
        this.activeFont = null;
        this.confirmDeleteFont = false;
        this.loaders.fonts = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    formatFontName(name) {
      const n = name.replace(/-|_/gi, ' ');
      return _.startCase(n);
    },
    makeFontCSS(file) {
      const fontName = path.basename(file).split('.')[0];
      const ext = path.extname(file);
      const formats = {
        '.otf': 'opentype',
        '.ttf': 'truetype',
        '.woff': 'woff',
        '.woff2': 'woff2',
      };
      return new File({
        name: 'font.css',
        type: 'text/css',
        buffer: new Buffer(`
          @font-face{
            font-family: '${fontName}';
            font-style: normal;
            font-weight: normal;
            src: url('${file}') format('${formats[ext]}');
          }`),
      });
    },
    async saveItem(key) {
      if (this.changed[key]) {
        this.loaders[key] = true;
        const notif = {
          id: this.guid(),
          type: 'success',
          message: 'Font successfully saved!',
        };
        try {
          const updates = [
            {
              key: 'mediaQueries',
              value: this.currentSite.mediaQueries,
            },
            {
              key: 'pageCSS',
              value: this.currentSite.pageCSS,
            },
          ];
          await this.$lightning([{
            name: 'updateSiteAttrs',
            payload: {
              siteId: this.currentSite._id,
              updates,
            },
          }]);
          this.$log.debug('successfully saved site changes');
          // since this is a site wide change, we need to regenerate the
          // site and push it to s3
          await this.callBuilder({
            publish: 'site',
            site: {
              _id: this.currentSite._id,
            },
          });
        } catch (err) {
          this.$log.error(err);
          notif.type = 'error';
          notif.message = err.message || err;
          await this._reportError({
            error: err,
            fn: 'save global settings',
          });
        } finally {
          this.loaders[key] = false;
          this.changed[key] = false;
          if (key === 'defaultFont') {
            this.initial.pageCSS = _.cloneDeep(this.currentSite.pageCSS);
            this.initial.mediaQueries = _.cloneDeep(this.currentSite.mediaQueries);
            this.$emit('set-initial', key);
          } else {
            this.initial[key] = _.cloneDeep(this.currentSite[key]);
          }
          this.$store.dispatch('addNotification', notif);
        }
      }
    },
    onCloseModal() {
      _.forOwn(this.changed, (changed, key) => {
        // if something was changed but not saved on close,
        // we need to revert it back
        if (changed) {
          this.cancelItem(key);
        }
      });
    },
    closeModal() {
      this.$emit('toggle-modal');
    },
    cancelItem(key) {
      if (this.changed[key]) {
        // we need special treatment based on what's being cancelled
        if (key === 'defaultFont') {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'pageCSS',
            value: _.cloneDeep(this.initial.pageCSS),
          });
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key: 'mediaQueries',
            value: _.cloneDeep(this.initial.mediaQueries),
          });
          this.callCssGenerator();
          this.display.fontFamily = this.getDefaultFont();
        }
      }
    },
    formatErrors(ers) {
      let string = '';
      _.forEach(ers, (er) => {
        string = string += ` ${er}`;
      });
      return string;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/global-options/fonts/index.js